import { ChangeDetectionStrategy, Component, computed, inject, Injector, input, signal } from '@angular/core'
import { DatePipe } from '@angular/common'
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'
import { AccountDetailStore } from '../account-detail/account-detail.store'
import { ChangePlanModalComponent } from '../change-plan-modal/change-plan-modal.component'
import { CommonService, ToastrType } from '@appShared/services'
import { ISubscription, ISubscriptionUpdateSubmission } from '@appShared/interfaces/[Model-based]'
import {
   BillingFrequency,
   BillingFrequency_,
   CancellationReason_,
   CancellationReasons,
   SubscriptionStatus,
   SubscriptionStatus_
} from '@appShared/services/lookup/[CodeGen]'

/** The inline step the rep is currently working through. */
type MembershipStep =
   | 'status'              // default: status banner + entry actions
   | 'chooseChange'        // radio list: pause 1mo / pause 3mo / cancel
   | 'confirmPause'        // inline confirm for the chosen pause option
   | 'confirmCancel'       // inline "are you sure" before reasons
   | 'chooseCancelReason'  // required cancellation-reason radio list
   | 'confirmResume'       // inline confirm for resume / un-pause / undo-cancel

/** Which resume confirmation copy to show. */
type ResumeKind = 'unpause' | 'resume' | 'undoCancel'

interface IMembershipChangeOption {
   key: 'pause1' | 'pause3' | 'cancel'
   description: string
   isCancel: boolean
   monthsToPause: number
   newStatusCode: SubscriptionStatus_
   resumeDate: string | null
}

@Component({
   selector: 'app-subscription-membership',
   templateUrl: './subscription-membership.component.html',
   styleUrls: ['./subscription-membership.component.less'],
   imports: [DatePipe],
   changeDetection: ChangeDetectionStrategy.OnPush
})
export class SubscriptionMembershipComponent {
   private readonly _store = inject(AccountDetailStore)
   private readonly _common = inject(CommonService)
   private readonly _modalService = inject(NgbModal)
   private readonly _injector = inject(Injector)

   /** The subscription to manage — passed in by the parent (account-detail's actionableSubscription). */
   public readonly subscription = input.required<ISubscription>()

   /* expose enums to the template */
   public readonly CancellationReason_ = CancellationReason_

   /* ----- source data ----- */
   /* The plan this subscription will default to at renewal. Derived locally from
      the subscription's own conversion options, mirroring how the store does it. */
   public readonly nextPlannedSubscriptionConversionOption = computed(() =>
      this.subscription()?.subscriptionConversionOptions?.find((option) => option.isPlanned)
   )
   public readonly isLoading = this._store.accountIsLoading
   public readonly errorMessage = this._store.accountErrorMessage
   public readonly isUpdating = this._store.membershipIsUpdating
   public readonly seatLimitIsUpdating = this._store.seatLimitIsUpdating

   /* ----- derived status ----- */
   public readonly statusDescription = computed(() =>
      SubscriptionStatus.toDomainModel(this.subscription()?.statusCode)?.description
   )
   public readonly statusClass = computed(() => {
      switch (this.subscription()?.statusCode) {
         case SubscriptionStatus_.Active:
            return 'subscription-status-active'
         case SubscriptionStatus_.Cancelled:
         case SubscriptionStatus_.Ended:
            return 'subscription-status-danger'
         case SubscriptionStatus_.Renewal:
            return 'subscription-status-info'
         case SubscriptionStatus_.OnHold:
         case SubscriptionStatus_.Paused:
            return 'subscription-status-warning'
         default:
            return ''
      }
   })

   public readonly isActive = computed(() => this.subscription()?.statusCode === SubscriptionStatus_.Active)
   public readonly isPaused = computed(() => this.subscription()?.statusCode === SubscriptionStatus_.Paused)
   public readonly isOnHold = computed(() => this.subscription()?.statusCode === SubscriptionStatus_.OnHold)
   public readonly isEnded = computed(() => this.subscription()?.statusCode === SubscriptionStatus_.Ended)
   public readonly isCancelled = computed(() => this.subscription()?.statusCode === SubscriptionStatus_.Cancelled)
   public readonly isPendingPause = computed(() => !!this.subscription()?.info?.isPendingPause)
   public readonly isPendingCancellation = computed(() => !!this.subscription()?.info?.isPendingCancellation)
   public readonly isMonthly = computed(() =>
      this.subscription()?.product?.billingFrequencyCode === BillingFrequency_.Monthly
   )
   public readonly isPartnerBilled = computed(() =>
      this.subscription()?.product?.billingFrequencyCode === BillingFrequency_.PartnerBilled
   )
   public readonly billingFrequencyDescription = computed(() =>
      BillingFrequency.toDomainModel(this.subscription()?.product?.billingFrequencyCode)?.description ?? ''
   )

   public readonly productName = computed(() => this.subscription()?.product?.displayName ?? '')
   public readonly nextPaymentDue = computed(() => this.subscription()?.nextPaymentDue)
   public readonly pausedUntil = computed(() => this.subscription()?.pausedUntil)
   public readonly cancellationEffective = computed(() => this.subscription()?.cancellationEffective)

   /** Is this subscription in a state the rep can act on (core monthly flow only)? */
   public readonly canManage = computed(() =>
      this.isMonthly() && !this.isOnHold() && !this.isEnded() && !this.isCancelled()
   )
   /** Show the "Pause or cancel / Extend pause or cancel" entry button? */
   public readonly canPauseExtendCancelPlan = computed(() =>
      this.canManage() && !this.isPendingCancellation() &&
      (this.isActive() || this.isPaused() || this.isPendingPause())
   )
   public readonly changeEntryLabel = computed(() =>
      (this.isPaused() || this.isPendingPause())
         ? 'Extend pause or cancel membership'
         : 'Pause or cancel membership'
   )

   /** Pause/cancel options, with resume dates + paused-context labels. */
   public readonly changeOptions = computed<IMembershipChangeOption[]>(() => {
      const isPausedContext = this.isPaused() || this.isPendingPause()
      const base = isPausedContext && this.pausedUntil() ? this.pausedUntil() : this.nextPaymentDue()

      return [
         {
            key: 'pause1',
            description: isPausedContext ? 'Extend for an additional month' : 'Pause for 1 month',
            isCancel: false,
            monthsToPause: 1,
            newStatusCode: SubscriptionStatus_.Paused,
            resumeDate: this._addMonths(base, 1)
         },
         {
            key: 'pause3',
            description: isPausedContext ? 'Extend for an additional 3 months' : 'Pause for 3 months',
            isCancel: false,
            monthsToPause: 3,
            newStatusCode: SubscriptionStatus_.Paused,
            resumeDate: this._addMonths(base, 3)
         },
         {
            key: 'cancel',
            description: 'Cancel Membership',
            isCancel: true,
            monthsToPause: 0,
            newStatusCode: SubscriptionStatus_.Cancelled,
            resumeDate: null
         }
      ]
   })

   public readonly cancellationReasons = CancellationReasons
      .filter(reason => reason.code !== CancellationReason_.Unknown)

   /* ----- UI flow state ----- */
   public readonly step = signal<MembershipStep>('status')
   public readonly selectedChange = signal<IMembershipChangeOption | null>(null)
   public readonly resumeKind = signal<ResumeKind | null>(null)
   public readonly selectedCancelReasonCode = signal<CancellationReason_ | null>(null)
   public readonly cancelReasonText = signal<string>('')

   /* ----- seat limit edit state ----- */
   public readonly seatLimit = computed(() => this.subscription()?.seatLimit)
   /* A default seat limit of exactly 1 marks a single-seat product —
      those neither show nor edit the limit */
   public readonly isMultiSeatProduct = computed(() => {
      const product = this.subscription()?.product
      return !!product && product.defaultSeatLimit !== 1
   })
   public readonly seatsUsed = computed(() => this.subscription()?.subscriptionSeats?.length ?? 0)
   /* The limit can't drop below the seats already occupied (the server enforces this too) */
   public readonly minSeatLimit = computed(() => Math.max(1, this.seatsUsed()))
   public readonly isEditingSeatLimit = signal<boolean>(false)
   public readonly seatLimitEdit = signal<number | null>(null)
   /* SeatLimit is a C# short on the server, so cap the input at short.MaxValue */
   public readonly canSaveSeatLimit = computed(() => {
      const value = this.seatLimitEdit()
      return value != null && Number.isInteger(value) &&
         value >= this.minSeatLimit() && value <= 32767 && value !== this.seatLimit()
   })

   public readonly showCancelReasonText = computed(() =>
      this.selectedCancelReasonCode() === CancellationReason_.FoundAlternative
   )
   public readonly canConfirmCancel = computed(() => this.selectedCancelReasonCode() != null)

   /*
   * private methods
   * */

   private _addMonths(baseDate: string | null | undefined, months: number): string | null {
      if (!baseDate) {
         return null
      }
      return this._common.dateTime.moment(new Date(baseDate)).add(months, 'months').format('MM/DD/YYYY')
   }

   private async _submit(submission: ISubscriptionUpdateSubmission, successMessage: string): Promise<void> {
      const result = await this._store.updateSubscription(submission)
      if (result.ok) {
         this.reset()
         this._common.messageUser(`<div class="text-center">${successMessage}</div>`)
      } else {
         this._common.messageUser(
            result.error ?? 'We were unable to update the membership. Please try again.',
            'Membership Update Failed',
            ToastrType.error
         )
      }
   }

   /*
   * public methods
   * */

   changePlan(): void {
      // Pass our injector so the modal can reach the route-scoped AccountDetailStore.
      this._modalService.open(ChangePlanModalComponent, {
         size: 'lg',
         backdrop: 'static',
         injector: this._injector
      })
   }

   reset(): void {
      this.step.set('status')
      this.selectedChange.set(null)
      this.resumeKind.set(null)
      this.selectedCancelReasonCode.set(null)
      this.cancelReasonText.set('')
   }

   startChange(): void {
      this.selectedChange.set(null)
      this.selectedCancelReasonCode.set(null)
      this.cancelReasonText.set('')
      this.step.set('chooseChange')
   }

   selectChange(option: IMembershipChangeOption): void {
      this.selectedChange.set(option)
      this.step.set(option.isCancel ? 'confirmCancel' : 'confirmPause')
   }

   confirmPause(): void {
      const option = this.selectedChange()
      const subscription = this.subscription()
      if (!option || option.isCancel || !subscription) {
         return
      }

      this._submit({
         subscriptionId: subscription.id,
         newStatusCode: SubscriptionStatus_.Paused,
         monthsToPause: option.monthsToPause,
         cancellationReasonCode: CancellationReason_.Unknown,
         cancellationReasonText: ''
      } as ISubscriptionUpdateSubmission, 'Membership Updated')
   }

   proceedToCancelReason(): void {
      this.selectedCancelReasonCode.set(null)
      this.cancelReasonText.set('')
      this.step.set('chooseCancelReason')
   }

   selectCancelReason(code: CancellationReason_): void {
      this.selectedCancelReasonCode.set(code)
      if (code !== CancellationReason_.FoundAlternative) {
         this.cancelReasonText.set('')
      }
   }

   confirmCancel(): void {
      const reasonCode = this.selectedCancelReasonCode()
      const subscription = this.subscription()
      if (reasonCode == null || !subscription) {
         return
      }

      this._submit({
         subscriptionId: subscription.id,
         newStatusCode: SubscriptionStatus_.Cancelled,
         monthsToPause: 0,
         cancellationReasonCode: reasonCode,
         cancellationReasonText: this.cancelReasonText()
      } as ISubscriptionUpdateSubmission, 'Membership Cancelled')
   }

   startResume(kind: ResumeKind): void {
      this.resumeKind.set(kind)
      this.step.set('confirmResume')
   }

   startEditSeatLimit(): void {
      this.seatLimitEdit.set(this.seatLimit() ?? null)
      this.isEditingSeatLimit.set(true)
   }

   cancelEditSeatLimit(): void {
      this.isEditingSeatLimit.set(false)
      this.seatLimitEdit.set(null)
   }

   async confirmSeatLimit(): Promise<void> {
      const seatLimit = this.seatLimitEdit()
      if (!this.canSaveSeatLimit() || seatLimit == null) {
         return
      }

      const result = await this._store.updateSeatLimit(seatLimit)
      if (result.ok) {
         this.cancelEditSeatLimit()
         this._common.messageUser('<div class="text-center">Seat Limit Updated</div>')
      } else {
         this._common.messageUser(
            result.error ?? 'We were unable to update the seat limit. Please try again.',
            'Seat Limit Update Failed',
            ToastrType.error
         )
      }
   }

   confirmResume(): void {
      const subscription = this.subscription()
      if (!subscription) {
         return
      }

      this._submit({
         subscriptionId: subscription.id,
         newStatusCode: SubscriptionStatus_.Active,
         monthsToPause: 0,
         cancellationReasonCode: CancellationReason_.Unknown,
         cancellationReasonText: ''
      } as ISubscriptionUpdateSubmission, 'Membership Updated')
   }
}
